import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { Elements as StripeElements } from '@stripe/react-stripe-js';

import useProRegister from './hooks/useProRegister';
import useStripePromise from '../../../hooks/useStripePromise';
import RegisterViewDetailsForm from './components/RegisterViewDetailsForm';
import RegisterCheckoutCart from './components/RegisterCheckoutCart';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import { isValidEmail } from '../../../util/email';
import { PRO_REGISTRATION_STEP } from './helpers';
import Logo from '../../../components/global/Logo';
import RegisterPhotoManagerForm from './components/RegisterPhotoManagerForm';
import Spinner from '../../../components/global/Spinner';
import RegisterPhotoFinderForm from './components/RegisterPhotoFinderForm';
import { isValidUsername } from '../../../util/account';
import ERROR_MESSAGES from '../../../constants/error-messages';
import Angle from '../../../components/global/Angle';
import Button from '../../../components/global/Button';
import SignUpLayout from '../../../components/global/SignUpLayout';

const RegisterView = () => {
  const stripePromise = useStripePromise();
  const {
    loading,
    onAccountPromoCodeVerify,
    photoManagerSubscriptionItems,
    defaultPersona,
    photoFinderSubscriptionItems = [],
    defaultPhotoFinderTier,
    defaultPhotoManagerItem,
    activeStep,
    onSubmit,
    loadingSubscriptionItems,
    onAccountUsernameVerify,
    accountPromoCode,
    canGoBack,
    onGoBack,
    defaultPromoCode,
  } = useProRegister();

  const onValidate = async values => {
    const errors = {};

    switch (activeStep) {
      case PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION: {
        if (values.promo_code.length > 0) {
          const enteredPromoCode = await onAccountPromoCodeVerify(
            values.promo_code,
          );

          if (
            !enteredPromoCode.is_valid ||
            ![
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
            ].includes(enteredPromoCode.type)
          ) {
            errors.promo_code =
              'The promo code you provided does not exist or is no longer active.';
          }
        }

        break;
      }
      case PRO_REGISTRATION_STEP.FREE_TRIAL:
      case PRO_REGISTRATION_STEP.DETAILS: {
        const { exists } = await onAccountUsernameVerify(values.username);

        if (!isValidEmail(values.email_address)) {
          errors.email_address = 'This email address is not valid.';
        }

        if (!isValidUsername(values.username)) {
          errors.username = ERROR_MESSAGES.E161;
        }

        if (exists) {
          errors.username = 'This username is already taken';
        }

        if (values.password.length <= 7) {
          errors.password = 'Password must be longer than 8 characters.';
        }

        break;
      }
      default:
        break;
    }

    if (Object.keys(errors).length > 0) {
      throw errors;
    }
  };

  const supportText = (
    <div className="mt-4 px-6">
      <p className="text-sm text-center">
        By continuing you agree to
        <br /> the{' '}
        <a href="/terms" target="_blank" rel="nofollow">
          Terms of Use
        </a>{' '}
        &{' '}
        <a href="/terms" target="_blank" rel="nofollow">
          Privacy Policy
        </a>
      </p>
      <p className="mb-0 mt-4 text-sm text-center">
        Need Help?{' '}
        <a href="mailto:sales@waldophotos.com">Contact sales &rarr;</a>
      </p>
    </div>
  );

  if (loadingSubscriptionItems) {
    return (
      <div className="content-wrap-escape">
        <div className="container">
          <div className="site-brand">
            <Logo />
          </div>

          <div className="d-flex p-5">
            <Spinner className="m-auto" />
          </div>
        </div>
      </div>
    );
  }

  const title = (() => {
    if (activeStep === PRO_REGISTRATION_STEP.FREE_TRIAL) {
      return 'Start your 30-Day free trial';
    }

    if (
      [
        PRO_REGISTRATION_STEP.DETAILS,
        PRO_REGISTRATION_STEP.PHOTO_FINDER_SUBSCRIPTION_SELECTION,
      ].includes(activeStep)
    ) {
      return 'Complete your profile';
    }

    if (activeStep === PRO_REGISTRATION_STEP.CART) {
      return 'Your payment details';
    }

    return 'Pick your plan';
  })();

  return (
    <StripeElements stripe={stripePromise}>
      <Helmet>
        <title>Register &ndash; Waldo</title>
      </Helmet>
      <SignUpLayout.Wrapper splitPaneClassNames="register-view-left-pane-container">
        <SignUpLayout.Content>
          <SignUpLayout.Title
            subtitle={
              canGoBack ? (
                <Button
                  onClick={onGoBack}
                  className="text-gray-blue-dark text-sm text-extra-bold"
                  btnStyle="link"
                >
                  <Angle direction="left" className="mr-1" /> Back
                </Button>
              ) : (
                <Fragment>Welcome to Waldo!</Fragment>
              )
            }
            title={title}
          />
          <div className="photo-manager-registration-form-wrapper">
            <Formik
              // Forces a re-render if the default photo manager
              // is updated through the URL somehow. At the moment,
              // Only possible via a URL promo code.
              key={defaultPhotoManagerItem?.id}
              initialValues={{
                first_name: '',
                last_name: '',
                organization_name: '',
                email_address: '',
                password: '',
                username: '',
                persona: defaultPersona ?? '',
                promo_code: '',
                default_promo_code: defaultPromoCode ?? '',

                // Checkout logic
                stripe_token: null,
                requires_payment: false,
                plan_id: defaultPhotoManagerItem?.id ?? null,
                tier_id: defaultPhotoFinderTier?.id ?? null,
              }}
              validate={onValidate}
              onSubmit={onSubmit}
              validateOnBlur={false}
              validateOnChange={false}
              render={({
                values,
                handleSubmit,
                handleChange,
                handleBlur,
                errors,
                touched = {},
              }) => {
                const selectedSubscriptionItem = photoManagerSubscriptionItems?.find(
                  item => item.id === values.plan_id,
                );

                return (
                  <Fragment>
                    {activeStep ===
                      PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION && (
                      <Fragment>
                        <RegisterPhotoManagerForm
                          values={values}
                          photoManagerSubscriptionItems={
                            photoManagerSubscriptionItems
                          }
                          handleSubmit={handleSubmit}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          errors={errors}
                          touched={touched}
                          accountPromoCode={accountPromoCode}
                        />
                        <p className="mb-0 mt-4 text-sm text-center">
                          Need Help?{' '}
                          <a href="mailto:sales@waldophotos.com">
                            Contact sales &rarr;
                          </a>
                        </p>
                      </Fragment>
                    )}
                    {activeStep ===
                      PRO_REGISTRATION_STEP.PHOTO_FINDER_SUBSCRIPTION_SELECTION && (
                      <RegisterPhotoFinderForm
                        forceOpen
                        values={values}
                        handleSubmit={handleSubmit}
                        handleChange={handleChange}
                        photoFinderSubscriptionItems={
                          photoFinderSubscriptionItems
                        }
                      />
                    )}
                    {[
                      PRO_REGISTRATION_STEP.FREE_TRIAL,
                      PRO_REGISTRATION_STEP.DETAILS,
                      PRO_REGISTRATION_STEP.PHOTO_FINDER_SUBSCRIPTION_SELECTION,
                    ].includes(activeStep) && (
                      <Fragment>
                        <RegisterViewDetailsForm
                          values={values}
                          handleSubmit={handleSubmit}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          errors={errors}
                          touched={touched}
                          loading={loading}
                          requiresCreditCard={
                            values.promo_code?.length > 0 ||
                            !!selectedSubscriptionItem
                          }
                        />
                        {supportText}
                      </Fragment>
                    )}

                    {activeStep === PRO_REGISTRATION_STEP.CART && (
                      <RegisterCheckoutCart
                        values={values}
                        handleSubmit={handleSubmit}
                        handleChange={handleChange}
                        photoFinderSubscriptionItems={
                          photoFinderSubscriptionItems
                        }
                        photoManagerSubscriptionItems={
                          photoManagerSubscriptionItems
                        }
                        submitting={loading}
                        supportText={supportText}
                        accountPromoCode={accountPromoCode}
                      />
                    )}
                  </Fragment>
                );
              }}
            />
          </div>
        </SignUpLayout.Content>
      </SignUpLayout.Wrapper>
    </StripeElements>
  );
};

export default RegisterView;
